import { useEffect, useState } from "react";
import type {
  AdminParticipationResponse,
  ParticipationPromptPublic,
  ParticipationResponseStatus,
  ParticipationSchedule,
} from "@tih/shared";
import {
  ApiError,
  adminFetchParticipationPrompts,
  adminFetchParticipationResponses,
  adminFetchParticipationSchedule,
  adminModerateParticipationResponse,
  adminSaveParticipationSchedule,
  adminUpdateParticipationPrompt,
} from "../../api/client";

type Tab = "pending" | "pastoral" | "prompts" | "weekly" | "proximity";

const PUBLIC_TABS: { key: Tab; label: string }[] = [
  { key: "pending", label: "Public queue" },
  { key: "pastoral", label: "Pastoral inbox" },
  { key: "prompts", label: "Prompts" },
  { key: "weekly", label: "Weekly schedule" },
  { key: "proximity", label: "Event proximity" },
];

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function AdminParticipationPage() {
  const [tab, setTab] = useState<Tab>("pending");
  const [responses, setResponses] = useState<AdminParticipationResponse[]>([]);
  const [prompts, setPrompts] = useState<ParticipationPromptPublic[]>([]);
  const [schedule, setSchedule] = useState<ParticipationSchedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function loadResponses() {
    if (tab === "pending") {
      const rows = await adminFetchParticipationResponses({ status: "PENDING" });
      setResponses(rows.filter((r) => r.promptKind === "OPEN_TEXT"));
    } else if (tab === "pastoral") {
      const rows = await adminFetchParticipationResponses({ status: "PRIVATE" });
      setResponses(rows.filter((r) => r.promptKind === "PASTORAL"));
    }
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      if (tab === "pending" || tab === "pastoral") {
        await loadResponses();
      } else if (tab === "prompts") {
        setPrompts(await adminFetchParticipationPrompts());
      } else if (tab === "weekly" || tab === "proximity") {
        const [sched, pr] = await Promise.all([
          adminFetchParticipationSchedule(),
          adminFetchParticipationPrompts(),
        ]);
        setSchedule(sched);
        setPrompts(pr);
      }
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  async function moderate(id: number, status: ParticipationResponseStatus) {
    setBusy(true);
    setError(null);
    try {
      await adminModerateParticipationResponse(id, { status });
      setSuccess(status === "PUBLISHED" ? "Published." : "Updated.");
      await loadResponses();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function togglePrompt(id: number, enabled: boolean) {
    setBusy(true);
    try {
      await adminUpdateParticipationPrompt(id, { enabled });
      setPrompts(await adminFetchParticipationPrompts());
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setBusy(false);
    }
  }

  async function saveSchedule(patch: Partial<ParticipationSchedule>) {
    if (!schedule) return;
    setBusy(true);
    setError(null);
    try {
      const next = await adminSaveParticipationSchedule(patch);
      setSchedule(next);
      setSuccess("Schedule saved.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Participation</h1>
        <p>Moderate voice wall answers, pastoral requests, and home spotlight schedules.</p>
      </header>

      <div className="admin-tabs" role="tablist">
        {PUBLIC_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            className={`admin-tab${tab === t.key ? " active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="form-error">{error}</div>}
      {success && <div className="form-success">{success}</div>}
      {loading ? (
        <p style={{ opacity: 0.6 }}>Loading…</p>
      ) : tab === "pending" || tab === "pastoral" ? (
        <ul className="admin-list">
          {responses.length === 0 ? (
            <li style={{ opacity: 0.6 }}>Nothing in this queue.</li>
          ) : (
            responses.map((r) => (
              <li key={r.id} className="admin-list-item">
                <div>
                  <strong>{r.promptTitle}</strong>
                  {r.name && <span> — {r.name}</span>}
                  {r.phone && <div>{r.phone}</div>}
                  {r.body && <p style={{ marginTop: 8 }}>{r.body}</p>}
                  <div className="admin-list-meta">{new Date(r.createdAt).toLocaleString()}</div>
                </div>
                {tab === "pending" && (
                  <div className="admin-list-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={busy}
                      onClick={() => void moderate(r.id, "PUBLISHED")}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={busy}
                      onClick={() => void moderate(r.id, "REJECTED")}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </li>
            ))
          )}
        </ul>
      ) : tab === "prompts" ? (
        <ul className="admin-list">
          {prompts.map((p) => (
            <li key={p.id} className="admin-list-item">
              <div>
                <strong>{p.title}</strong>
                <div className="admin-list-meta">
                  {p.slug} · {p.kind} · {p.family}
                </div>
              </div>
              <label className="admin-check">
                <input
                  type="checkbox"
                  checked={p.enabled}
                  disabled={busy}
                  onChange={(e) => void togglePrompt(p.id, e.target.checked)}
                />
                Enabled
              </label>
            </li>
          ))}
        </ul>
      ) : schedule ? (
        <div className="admin-form-stack" style={{ maxWidth: 720 }}>
          {tab === "weekly" && (
            <>
              <h2 className="sec-title sec-title-sm">Weekly spotlight</h2>
              {DAY_LABELS.map((label, i) => (
                <label key={label}>
                  <span>{label}</span>
                  <select
                    value={schedule.weeklySpotlight[String(i) as keyof typeof schedule.weeklySpotlight]}
                    onChange={(e) => {
                      const weeklySpotlight = {
                        ...schedule.weeklySpotlight,
                        [String(i)]: e.target.value,
                      } as ParticipationSchedule["weeklySpotlight"];
                      void saveSchedule({ weeklySpotlight });
                    }}
                  >
                    {prompts.filter((p) => p.enabled).map((p) => (
                      <option key={p.slug} value={p.slug}>
                        {p.title}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </>
          )}
          {tab === "proximity" && (
            <>
              <h2 className="sec-title sec-title-sm">Event proximity map</h2>
              <p style={{ opacity: 0.75, marginBottom: 16 }}>
                Home uses these prompts during approach, countdown, and eve phases (
                {schedule.approachDays} days before the event).
              </p>
              {Object.entries(schedule.eventProximity).map(([range, slug]) => (
                <label key={range}>
                  <span>{range} days out</span>
                  <select
                    value={slug}
                    onChange={(e) => {
                      void saveSchedule({
                        eventProximity: { ...schedule.eventProximity, [range]: e.target.value },
                      });
                    }}
                  >
                    {prompts.filter((p) => p.enabled).map((p) => (
                      <option key={p.slug} value={p.slug}>
                        {p.title}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
              <label>
                <span>Announce phase starts (days out)</span>
                <input
                  type="number"
                  min={1}
                  max={90}
                  value={schedule.announceDays}
                  onChange={(e) =>
                    void saveSchedule({ announceDays: Number(e.target.value) || 30 })
                  }
                />
              </label>
              <label>
                <span>Approach phase starts (days out)</span>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={schedule.approachDays}
                  onChange={(e) =>
                    void saveSchedule({ approachDays: Number(e.target.value) || 14 })
                  }
                />
              </label>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
